import {
  Button,
  Col,
  Empty,
  Input,
  Row,
  Select,
  Skeleton,
  Tag,
  Typography,
} from "antd";
import {
  RollbackOutlined,
  SearchOutlined,
  CarOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import { errorMsg } from "../../Utils/Utils";
import { toServiceBoxVin, toServiceBoxProducts } from "./Api";

const { Option } = Select;

const initials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const ServiceBox = () => {
  const [vin, setVin] = useState("");
  const [lang, setLang] = useState("fr");
  const [loading, setLoading] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [requiresSelection, setRequiresSelection] = useState(false);
  const [families, setFamilies] = useState([]);
  const [genarts, setGenarts] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedGenart, setSelectedGenart] = useState(null);
  const [products, setProducts] = useState(null);
  const [searchDone, setSearchDone] = useState(false);

  const identify = async (v, vehicleId) => {
    setLoading(true);
    setVehicles([]);
    setFamilies([]);
    setGenarts([]);
    setSelectedFamily(null);
    setSelectedGenart(null);
    setProducts(null);
    try {
      const res = await toServiceBoxVin(v, lang, vehicleId);
      if (!res || !res.servicebox) return;
      const sb = res.servicebox;
      setVehicle(sb.vehicle);
      setRequiresSelection(sb.requiresVehicleSelection);
      if (sb.vehicles && sb.vehicles.length > 0) {
        setVehicles(sb.vehicles);
      }
      if (sb.families && sb.families.length > 0) {
        setFamilies(sb.families);
      }
      setSearchDone(true);
    } catch (e) {
      errorMsg(
        e.response?.data?.errors || "Impossible d'identifier le véhicule",
      );
    }
    setLoading(false);
  };

  const selectFamily = (family) => {
    setSelectedFamily(family);
    setSelectedGenart(null);
    setProducts(null);
    setGenarts((family.genarts || []).filter((g) => !g.disabled));
  };

  const selectGenart = (genart) => {
    setSelectedGenart(genart);
    loadProducts(genart);
  };

  const loadProducts = async (genart) => {
    setLoading(true);
    try {
      const res = await toServiceBoxProducts(
        String(vehicle.id || vehicle.vehicleId),
        selectedFamily.id,
        genart.id,
        selectedFamily.name,
        genart.name,
        !!genart.universal,
      );
      setProducts(res && res.products ? res.products : { products: [] });
    } catch (e) {
      setProducts({ products: [] });
      errorMsg(
        e.response?.data?.errors || "Impossible de charger les produits",
      );
    }
    setLoading(false);
  };

  const reset = () => {
    setVehicle(null);
    setVehicles([]);
    setFamilies([]);
    setGenarts([]);
    setSelectedFamily(null);
    setSelectedGenart(null);
    setProducts(null);
    setSearchDone(false);
    setRequiresSelection(false);
  };

  const specGroups = vehicle
    ? [
        {
          label: "Identifiants",
          items: [
            { k: "VIN", v: vehicle.vin },
            { k: "ID", v: vehicle.id },
          ],
        },
        {
          label: "Modèle",
          items: [
            vehicle.brandName ? { k: "Marque", v: vehicle.brandName } : null,
            vehicle.modelName ? { k: "Modèle", v: vehicle.modelName } : null,
            vehicle.fuelType ? { k: "Carburant", v: vehicle.fuelType } : null,
          ].filter(Boolean),
        },
      ].filter(Boolean)
    : [];

  return (
    <div>
      {/* ---------- Recherche VIN ---------- */}
      <div className="npl-card npl-card-pad">
        <div className="npl-section-label" style={{ marginBottom: 14 }}>
          Recherche par VIN — Service Box
        </div>
        <div className="npl-searchbar">
          <Select
            className="npl-lang-select"
            value={lang}
            onChange={(e) => setLang(e)}
          >
            <Option value="fr">Français</Option>
            <Option value="en">English</Option>
            <Option value="de">Deutsch</Option>
          </Select>
          <Input
            className="npl-vin-input"
            style={{ flex: 1, minWidth: 260, maxWidth: 480 }}
            prefix={<SearchOutlined className="npl-search-icon" />}
            placeholder="Saisir le VIN (17 caractères)"
            maxLength={17}
            value={vin}
            onChange={(e) => setVin(e.target.value.toUpperCase())}
            onPressEnter={(e) => {
              reset();
              identify(e.target.value);
            }}
          />
          <Button
            type="primary"
            className="npl-btn-primary"
            icon={<CarOutlined />}
            loading={loading}
            onClick={() => {
              reset();
              identify(vin);
            }}
          >
            Identifier
          </Button>
        </div>
        {searchDone && (vehicle || families.length > 0) && (
          <div style={{ marginTop: 14 }}>
            <Button
              icon={<RollbackOutlined />}
              className="npl-btn-secondary"
              onClick={reset}
            >
              Nouvelle recherche
            </Button>
          </div>
        )}
      </div>

      <Skeleton active loading={loading} />

      {/* ---------- Choix du véhicule ---------- */}
      {!loading && requiresSelection && vehicles.length > 0 && (
        <div className="npl-card">
          <div className="npl-section-title" style={{ marginBottom: 12 }}>
            <CarOutlined style={{ color: "#059669" }} /> Choix du véhicule
          </div>
          <Row gutter={[12, 12]}>
            {vehicles.map((el) => (
              <Col key={el.id} md={8} sm={12} xs={24}>
                <div
                  className="npl-selectable"
                  onClick={() => identify(vin, String(el.id))}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: 8,
                    }}
                  >
                    <span className="npl-brand-logo">
                      {initials(el.fullName)}
                    </span>
                    <p className="npl-selectable-title">{el.fullName}</p>
                  </div>
                  <span className="npl-micro-btn">
                    Sélectionner <ArrowRightOutlined style={{ fontSize: 11 }} />
                  </span>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      )}

      {/* ---------- Fiche véhicule ---------- */}
      {!loading && searchDone && !requiresSelection && vehicle && (
        <div className="npl-card npl-card-pad">
          <div className="npl-vehicle">
            <div className="npl-vehicle-brand">
              <span className="npl-brand-emblem">
                {initials(vehicle.brandName || vehicle.fullName)}
              </span>
              <span className="npl-vehicle-model">
                {vehicle.fullName || vehicle.modelName || vehicle.vin}
              </span>
              <span className="npl-vehicle-vin">{vehicle.vin}</span>
            </div>
            <div className="npl-spec-grid">
              {specGroups.map((group) => (
                <div key={group.label} className="npl-spec-group">
                  {group.items.map((item, i) => (
                    <div key={i}>
                      <div className="npl-spec-label">{item.k}</div>
                      <div className="npl-spec-value">{item.v}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ---------- Familles ---------- */}
      {!loading && searchDone && !requiresSelection && families.length > 0 && (
        <div className="npl-card">
          <div className="npl-section-title" style={{ marginBottom: 12 }}>
            Familles
          </div>
          <Row gutter={[12, 12]}>
            {families
              .filter((f) => !f.disabled)
              .map((f) => (
                <Col key={f.id} md={6} sm={8} xs={12}>
                  <div
                    className={
                      "npl-selectable" +
                      (selectedFamily && selectedFamily.id === f.id
                        ? " npl-selectable--active"
                        : "")
                    }
                    onClick={() => selectFamily(f)}
                  >
                    <p className="npl-selectable-title">{f.name}</p>
                    {f.availableGenarts != null && (
                      <div className="npl-selectable-sub">
                        <span className="npl-badge-pill">
                          {f.availableGenarts} genart(s)
                        </span>
                      </div>
                    )}
                  </div>
                </Col>
              ))}
          </Row>
          {selectedFamily && (selectedFamily.subfamilies || []).length > 0 && (
            <>
              <div
                className="npl-section-label"
                style={{ marginTop: 20, marginBottom: 10 }}
              >
                Sous-familles
              </div>
              <Row gutter={[12, 12]}>
                {selectedFamily.subfamilies
                  .filter((f) => !f.disabled)
                  .map((f) => (
                    <Col key={f.id} md={6} sm={8} xs={12}>
                      <div
                        className={
                          "npl-selectable" +
                          (selectedFamily && selectedFamily.id === f.id
                            ? " npl-selectable--active"
                            : "")
                        }
                        onClick={() => selectFamily(f)}
                      >
                        <p className="npl-selectable-title">{f.name}</p>
                      </div>
                    </Col>
                  ))}
              </Row>
            </>
          )}
        </div>
      )}

      {/* ---------- Genarts ---------- */}
      {!loading && selectedFamily && genarts.length > 0 && (
        <div className="npl-card">
          <div className="npl-section-title" style={{ marginBottom: 12 }}>
            Genart
          </div>
          <Select
            className="npl-lang-select"
            style={{ width: "100%", maxWidth: 420 }}
            placeholder="Choisir un genart"
            showSearch
            value={selectedGenart ? selectedGenart.id : undefined}
            onChange={(id) => selectGenart(genarts.find((g) => g.id === id))}
            optionFilterProp="children"
          >
            {genarts.map((g) => (
              <Option key={g.id} value={g.id}>
                {g.name}
              </Option>
            ))}
          </Select>
        </div>
      )}

      {/* ---------- Produits ---------- */}
      {!loading && products && (
        <div className="npl-card" style={{ padding: 0, overflow: "hidden" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 16px",
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            <span className="npl-section-title">Produits</span>
            <span className="npl-badge-pill" style={{ marginLeft: "auto" }}>
              {products.products ? products.products.length : 0} produit(s)
            </span>
          </div>
          {products.products && products.products.length === 0 && (
            <div className="npl-empty-box">
              <Empty description="Aucun produit avec référence disponible" />
            </div>
          )}
          {products.products && products.products.length > 0 && (
            <div className="npl-table-wrap">
              <table className="npl-table">
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>Image</th>
                    <th>Référence</th>
                    <th>Référence OE</th>
                    <th>Marque</th>
                    <th style={{ width: 110 }}>Prix</th>
                    <th style={{ width: 100 }}>Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {products.products.map((p) => (
                    <tr key={p.id} style={{ cursor: "default" }}>
                      <td>
                        {p.imageUrl ? (
                          <img
                            className="npl-prod-img"
                            src={p.imageUrl}
                            alt={p.reference}
                          />
                        ) : (
                          <span style={{ color: "#cbd5e1" }}>—</span>
                        )}
                      </td>
                      <td>
                        <span className="npl-cell-mono">{p.reference}</span>
                      </td>
                      <td>
                        <span className="npl-cell-mono" style={{ fontWeight: 500, color: "#64748b" }}>
                          {p.oeRef || "—"}
                        </span>
                      </td>
                      <td>
                        <span className="npl-cell-main">{p.brand || "—"}</span>
                      </td>
                      <td>
                        {p.price != null ? (
                          <span className="npl-price">
                            {Number(p.price).toLocaleString("fr-FR")}
                          </span>
                        ) : (
                          <span style={{ color: "#cbd5e1" }}>—</span>
                        )}
                      </td>
                      <td>
                        {p.stock != null ? (
                          <Tag color={p.stock > 0 ? "green" : "red"}>
                            {p.stock}
                          </Tag>
                        ) : (
                          <span style={{ color: "#cbd5e1" }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ---------- États vides ---------- */}
      {!loading && selectedFamily && genarts.length === 0 && (
        <div className="npl-card">
          <Empty description="Aucun genart disponible pour cette famille" />
        </div>
      )}

      {!loading &&
        searchDone &&
        !requiresSelection &&
        families.length === 0 && (
          <div className="npl-card">
            <Empty description="Aucune famille disponible" />
          </div>
        )}

      {!loading && !searchDone && (
        <div className="npl-card">
          <div className="npl-empty-box">
            <Typography.Text type="secondary">
              Identifiez un véhicule par VIN pour parcourir les familles de
              produits
            </Typography.Text>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceBox;